import { assert } from 'zf-infra';
import { net } from '../net';
import { CommonError } from '../error';
import { HttpErrCode } from '../types';
import type { AnyObject } from 'zf-types';
import type { CustomDescriptor, Option } from './types';

/**
 * 用于控制请求时的loading状态 改为使用的是swc编译 （装饰器提案 2022-03 版本）
 *
 *  可以通过装饰器参数指定loading状态的属性名，默认loading属性名为 "loading"
 *  方法调用完成后，会自动将loading状态恢复为false。
 *
 *  同一时期更新系请求(PUT, DELETE, POST)仅允许存在一个，否则会抛出409异常。
 * @param option
 * @returns
 */
export function loading<Target, Params extends unknown[], Return>(option?: string | Option) {
  return function(
    target: Target,
    context: ClassMethodDecoratorContext
  ): CustomDescriptor<Return> {
    const params = getOption(option);

    if (context.kind === 'method') {
      return async function(this: AnyObject, ...args: Params): Promise<Return> {
        const [loadingState, loading] = params.property.split('.');
        assert(
          this[params.property] != null || this?.[loadingState]?.[loading] != null,
          `loading property is not defined: ${params.property}`
        );

        // 检测是否有正在进行的更新请求
        if (net.updating) {
          throw new CommonError('The previous update operation is not completed.', HttpErrCode.CONFLICT);
        }

        // 自动开启loading时，调用方法前设置loading状态为true
        if (params.auto) {
          setLoading(this, params.property, true);
        }

        // 恢复loading状态
        try {
          return await ((target as AnyObject).apply(this, args) as Promise<Return>);
        } finally {
          setLoading(this, params.property, false);
        }
      };
    }
  };
}

function getOption(option?: string | Option): Option {
  if (option == null) {
    return { property: 'loading', auto: true };
  }

  if (typeof option === 'string') {
    return { property: option, auto: true };
  }

  return option;
}

function setLoading(store: AnyObject, property: string, value: boolean): void {
  const [loadingState, loading] = property.split('.');
  if (loadingState != null && loading != null) {
    store[loadingState][loading] = value;
    return;
  }

  store[property] = value;
}
